import { useEffect, useState } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  updateDoc, doc, writeBatch
} from 'firebase/firestore';
import {
  Drawer, Box, Typography, IconButton, List, ListItem,
  ListItemText, Chip, Divider, Button, Tooltip
} from '@mui/material';
import { Close, DoneAll, NotificationsNone } from '@mui/icons-material';
import { db } from './firebase';
import { useAuth } from './AuthContext';

const TIPO_LABEL = { '1g': '1 giorno', '1h': '1 ora', '15m': '15 min' };
const TIPO_COLOR = { '1g': 'info', '1h': 'warning', '15m': 'error' };

function fmtAge(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diff < 1)  return 'Adesso';
  if (diff < 60) return `${diff} min fa`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

export default function NotificheDrawer({ open, onClose }) {
  const { staff } = useAuth();
  const [notifiche, setNotifiche] = useState([]);

  useEffect(() => {
    if (!staff?.nome) return;
    if (!open && notifiche.length > 0) return;
    const q = query(
      collection(db, 'crm_notifiche'),
      where('owner', '==', staff.nome),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, s =>
      setNotifiche(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [staff]);

  const markRead = async (id) => {
    await updateDoc(doc(db, 'crm_notifiche', id), { letta: true });
  };

  const markAllRead = async () => {
    const unread = notifiche.filter(n => !n.letta);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    unread.forEach(n => batch.update(doc(db, 'crm_notifiche', n.id), { letta: true }));
    await batch.commit();
  };

  const unreadCount = notifiche.filter(n => !n.letta).length;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 380 } } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsNone />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
            Notifiche {unreadCount > 0 && `(${unreadCount} nuove)`}
          </Typography>
        </Box>
        <Box>
          {unreadCount > 0 && (
            <Tooltip title="Segna tutte come lette">
              <IconButton size="small" onClick={markAllRead} sx={{ mr: 0.5 }}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>
      </Box>

      {/* Lista */}
      {notifiche.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <NotificationsNone sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body2">Nessuna notifica.</Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {notifiche.map((n, i) => (
            <Box key={n.id}>
              <ListItem
                alignItems="flex-start"
                onClick={() => !n.letta && markRead(n.id)}
                sx={{
                  bgcolor: n.letta ? 'transparent' : 'action.hover',
                  cursor: n.letta ? 'default' : 'pointer',
                  transition: 'background 0.2s',
                  py: 1.5, px: 2,
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip
                        label={TIPO_LABEL[n.tipo] || n.tipo}
                        size="small"
                        color={TIPO_COLOR[n.tipo] || 'default'}
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                      {!n.letta && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', ml: 'auto' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: n.letta ? 400 : 600 }}>
                        {n.messaggio}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAge(n.createdAt)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {i < notifiche.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      )}

      {notifiche.length > 0 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button fullWidth size="small" onClick={markAllRead} disabled={unreadCount === 0}>
            Segna tutte come lette
          </Button>
        </Box>
      )}
    </Drawer>
  );
}